/**
 * `components.MuiIconButton` — contained / outlined / tonal / shape variants and icon sizing.
 * Discoverability: `MuiIconButton`, `IconButton`, `variant`, `shape`, `shape: 'rounded'`.
 *
 * @see https://mui.com/material-ui/api/icon-button/
 *
 */
import { alpha } from '@mui/material/styles';
import type { Components, Theme } from '@mui/material/styles';

export function muiIconButton(theme: Theme): Pick<Components<Theme>, 'MuiIconButton'> {
  return {
    MuiIconButton: {
      defaultProps: {
        size: 'medium',
      },
      variants: [
        // ============================================================================
        // CONTAINED VARIANTS
        // ============================================================================

        // Contained - Primary
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
            '&.Mui-disabled': {
              color: theme.palette.primary.contrastText,
              backgroundColor: theme.palette.primary.main,
            },
          },
        },
        // Contained - Secondary
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            color: theme.palette.secondary.contrastText,
            backgroundColor: theme.palette.secondary.main,
            '&:hover': {
              backgroundColor: theme.palette.secondary.dark,
            },
            '&.Mui-disabled': {
              color: theme.palette.secondary.contrastText,
              backgroundColor: theme.palette.secondary.main,
            },
          },
        },
        // Contained - Error
        {
          props: { variant: 'contained', color: 'error' },
          style: {
            color: theme.palette.error.contrastText,
            backgroundColor: theme.palette.error.main,
            '&:hover': {
              backgroundColor: theme.palette.error.dark,
            },
            '&.Mui-disabled': {
              color: theme.palette.error.contrastText,
              backgroundColor: theme.palette.error.main,
            },
          },
        },
        // Contained - Warning
        {
          props: { variant: 'contained', color: 'warning' },
          style: {
            color: theme.palette.warning.contrastText,
            backgroundColor: theme.palette.warning.main,
            '&:hover': {
              backgroundColor: theme.palette.warning.dark,
            },
            '&.Mui-disabled': {
              color: theme.palette.warning.contrastText,
              backgroundColor: theme.palette.warning.main,
            },
          },
        },
        // Contained - Info
        {
          props: { variant: 'contained', color: 'info' },
          style: {
            color: theme.palette.info.contrastText,
            backgroundColor: theme.palette.info.main,
            '&:hover': {
              backgroundColor: theme.palette.info.dark,
            },
            '&.Mui-disabled': {
              color: theme.palette.info.contrastText,
              backgroundColor: theme.palette.info.main,
            },
          },
        },
        // Contained - Success
        {
          props: { variant: 'contained', color: 'success' },
          style: {
            color: theme.palette.success.contrastText,
            backgroundColor: theme.palette.success.main,
            '&:hover': {
              backgroundColor: theme.palette.success.dark,
            },
            '&.Mui-disabled': {
              color: theme.palette.success.contrastText,
              backgroundColor: theme.palette.success.main,
            },
          },
        },
        // Contained - Default
        {
          props: { variant: 'contained', color: 'default' },
          style: {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.grey[300],
            '&:hover': {
              backgroundColor: theme.palette.grey[400],
            },
            '&.Mui-disabled': {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.grey[300],
            },
          },
        },

        // ============================================================================
        // OUTLINED VARIANTS
        // ============================================================================

        // Outlined - Primary
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            border: '1px solid',
            color: theme.palette.primary.main,
            background: theme.palette.background.paper,
            borderColor: alpha(theme.palette.primary.main, 0.5),
            '&:hover': {
              color: theme.palette.primary.main,
              backgroundColor: theme.palette.primary.light,
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
            },
          },
        },
        // Outlined - Secondary
        {
          props: { variant: 'outlined', color: 'secondary' },
          style: {
            border: '1px solid',
            color: theme.palette.secondary.main,
            background: theme.palette.background.paper,
            borderColor: alpha(theme.palette.secondary.main, 0.5),
            '&:hover': {
              color: theme.palette.secondary.main,
              backgroundColor: theme.palette.secondary.light,
              borderColor: theme.palette.secondary.main,
            },
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: theme.palette.secondary.main,
              color: theme.palette.secondary.main,
            },
          },
        },
        // Outlined - Error
        {
          props: { variant: 'outlined', color: 'error' },
          style: {
            border: '1px solid',
            color: theme.palette.error.main,
            background: theme.palette.background.paper,
            borderColor: alpha(theme.palette.error.main, 0.5),
            '&:hover': {
              color: theme.palette.error.main,
              backgroundColor: theme.palette.error.light,
              borderColor: theme.palette.error.main,
            },
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: theme.palette.error.main,
              color: theme.palette.error.main,
            },
          },
        },
        // Outlined - Warning
        {
          props: { variant: 'outlined', color: 'warning' },
          style: {
            border: '1px solid',
            color: theme.palette.warning.main,
            background: theme.palette.background.paper,
            borderColor: alpha(theme.palette.warning.main, 0.5),
            '&:hover': {
              color: theme.palette.warning.main,
              backgroundColor: theme.palette.warning.light,
              borderColor: theme.palette.warning.main,
            },
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: theme.palette.warning.main,
              color: theme.palette.warning.main,
            },
          },
        },
        // Outlined - Info
        {
          props: { variant: 'outlined', color: 'info' },
          style: {
            border: '1px solid',
            color: theme.palette.info.main,
            background: theme.palette.background.paper,
            borderColor: alpha(theme.palette.info.main, 0.5),
            '&:hover': {
              color: theme.palette.info.main,
              backgroundColor: theme.palette.info.light,
              borderColor: theme.palette.info.main,
            },
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: theme.palette.info.main,
              color: theme.palette.info.main,
            },
          },
        },
        // Outlined - Success
        {
          props: { variant: 'outlined', color: 'success' },
          style: {
            border: '1px solid',
            color: theme.palette.success.main,
            background: theme.palette.background.paper,
            borderColor: alpha(theme.palette.success.main, 0.5),
            '&:hover': {
              color: theme.palette.success.main,
              backgroundColor: theme.palette.success.light,
              borderColor: theme.palette.success.main,
            },
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: theme.palette.success.main,
              color: theme.palette.success.main,
            },
          },
        },
        // Outlined - Default
        {
          props: { variant: 'outlined', color: 'default' },
          style: {
            border: '1px solid',
            color: theme.palette.text.primary,
            background: theme.palette.background.paper,
            borderColor: theme.palette.border.main,
            '&:hover': {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.border.dark,
            },
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: theme.palette.border.main,
              color: theme.palette.text.primary,
            },
          },
        },

        // ============================================================================
        // TONAL VARIANTS
        // ============================================================================

        // Tonal - Primary
        {
          props: { variant: 'tonal', color: 'primary' },
          style: {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.main,
            '&:hover': {
              color: theme.palette.primary.dark,
              background: theme.palette.secondary.light,
              '& .MuiSvgIcon-root': {
                color: theme.palette.primary.dark,
              },
            },
          },
        },
        // Tonal - Secondary
        {
          props: { variant: 'tonal', color: 'secondary' },
          style: {
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.secondary.main,
            '&:hover': {
              color: theme.palette.secondary.main,
              backgroundColor: alpha(theme.palette.secondary.main, 0.4),
              '& .MuiSvgIcon-root': {
                color: theme.palette.secondary.main,
              },
            },
          },
        },
        // Tonal - Error
        {
          props: { variant: 'tonal', color: 'error' },
          style: {
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.main,
            '&:hover': {
              color: theme.palette.error.dark,
              backgroundColor: alpha(theme.palette.error.main, 0.4),
              '& .MuiSvgIcon-root': {
                color: theme.palette.error.main,
              },
            },
          },
        },
        // Tonal - Warning
        {
          props: { variant: 'tonal', color: 'warning' },
          style: {
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.main,
            '&:hover': {
              color: theme.palette.warning.main,
              backgroundColor: alpha(theme.palette.warning.main, 0.4),
              '& .MuiSvgIcon-root': {
                color: theme.palette.warning.main,
              },
            },
          },
        },
        // Tonal - Info
        {
          props: { variant: 'tonal', color: 'info' },
          style: {
            backgroundColor: theme.palette.info.light,
            color: theme.palette.info.main,
            '&:hover': {
              color: theme.palette.info.main,
              backgroundColor: alpha(theme.palette.info.main, 0.4),
              '& .MuiSvgIcon-root': {
                color: theme.palette.info.main,
              },
            },
          },
        },
        // Tonal - Success
        {
          props: { variant: 'tonal', color: 'success' },
          style: {
            backgroundColor: theme.palette.success.light,
            color: theme.palette.success.main,
            '&:hover': {
              color: theme.palette.success.main,
              backgroundColor: alpha(theme.palette.success.main, 0.4),
              '& .MuiSvgIcon-root': {
                color: theme.palette.success.main,
              },
            },
          },
        },
        // Tonal - Default
        {
          props: { variant: 'tonal', color: 'default' },
          style: {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            '&:hover': {
              color: theme.palette.primary.main,
              background: theme.palette.primary.light,
            },
          },
        },

        // ============================================================================
        // SHAPE VARIANTS
        // ============================================================================

        // Size-specific rounded shapes
        {
          props: { size: 'small', shape: 'rounded' },
          style: {
            borderRadius: 6,
          },
        },
        {
          props: { size: 'medium', shape: 'rounded' },
          style: {
            borderRadius: 8,
          },
        },
        {
          props: { size: 'large', shape: 'rounded' },
          style: {
            borderRadius: 12,
          },
        },
      ],
      styleOverrides: {
        root: ({ ownerState }) => ({
          // Icon size configurations
          '& .MuiSvgIcon-root': {
            ...(ownerState.size === 'small' && {
              fontSize: '18px',
            }),
            ...(ownerState.size === 'medium' && {
              fontSize: '20px',
            }),
            ...(ownerState.size === 'large' && {
              fontSize: '22px',
            }),
          },
          // Size-specific border radius configurations (for circular shape)
          ...(ownerState.shape !== 'rounded' &&
            ownerState.size === 'small' && {
              borderRadius: '6px',
            }),
          ...(ownerState.shape !== 'rounded' &&
            ownerState.size === 'medium' && {
              borderRadius: '8px',
            }),
          ...(ownerState.shape !== 'rounded' &&
            ownerState.size === 'large' && {
              borderRadius: '12px',
            }),
          // Circular shape (default) - use 50% for perfect circle
          ...(ownerState.shape !== 'rounded' && {
            borderRadius: '50%',
          }),
          // Fix outlined variant sizing - ensure consistent button size
          ...(ownerState.variant === 'outlined' && {
            border: '1px solid',
            padding: ownerState.size === 'small' ? '5px' : ownerState.size === 'large' ? '9px' : '7px',
          }),
          ...(ownerState.variant !== 'outlined' && {
            border: '0',
            padding: ownerState.size === 'small' ? '6px' : ownerState.size === 'large' ? '10px' : '8px',
          }),
          '&.Mui-disabled': {
            opacity: theme.palette.action.disabledOpacity,
          },
        }),
      },
    },
  };
}
